import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  Input,
  Text,
  useToast,
  Flex,
} from '@chakra-ui/react'
import { STATUS } from 'features/common/status'
import { Controller, UseFormReturn, useWatch } from 'react-hook-form'
import { useState, useRef, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useClient } from 'utils/auth-context'
import { MdOutlineCancel } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { BiUpload, BiXCircle } from 'react-icons/bi'
import { WORK_ORDER } from '../workOrder.i18n'
import { dateFormat } from 'utils/date-time-utils'
import autoTable from 'jspdf-autotable'
import { currencyFormatter } from 'utils/string-formatters'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import round from 'lodash/round'
import { isValidAndNonEmpty } from 'utils'
import { CustomCheckBox } from './assigned-items'
import { useProjectTypeSelectOptions } from 'api/pc-projects'
import { Select as ReactSelect } from 'chakra-react-select'
import { inputBorderLeftStyle, inputFocusStateStyle } from 'theme/common-style'
import React from 'react'
import { addImages } from 'api/wo-estimates'
import { TicketTimeSlots } from 'features/ticket-page/order-intake-form-edit'

const swoPrefix = '/smartwo/api'

export type LineItems = {
  id?: number | string | null
  sku: string
  productName: string
  details?: string
  description?: string
  price?: string | number | null
  unitPrice?: string | number | null
  quantity: number | string | null
  totalPrice?: string | number | null
  isAssigned?: boolean
  projectId?: string | number | null
  createdBy?: string
  createdDate?: string
  modifiedBy?: string
  modifiedDate?: string
  smartLineItemId?: string | number | null
  source?: string
  isVerified?: boolean
  isCompleted?: boolean
  action?: string
  document?: any
  vendorAmount?: number | null
  profit?: number | null
  location?: string | null
}

export type SWOProject = {
  id: number | string
  projectId: number | string
  textractJobId: string
  status: string
  documentUrl: string
  createdBy: string
  createdDate: string
  modifiedBy: string
  modifiedDate: string
}

export type selectedCell = { id: string; value: string }

export const getRemovedItems = (formValues, workOrderAssignedItems) => {
  if (formValues?.cancel?.value === 35) {
    return formValues.assignedItems
  }

  /* checking which  smart work order items existed in workOrder but now are not present in the form. They have to unassigned*/
  const formAssignedItemsIds = formValues?.assignedItems?.map(s => s.id)
  const deletedItems = [...workOrderAssignedItems?.filter(items => !formAssignedItemsIds?.includes(items.id))]
  return deletedItems
}

export const getUnAssignedItems = (formValues, workOrderAssignedItems) => {
  /* check if work order is being cancelled we should unassign all line items */
  if (formValues?.cancel?.value === 35) {
    return formValues.assignedItems?.filter(a => 'smartLineItemId' in a && !!a.smartLineItemId)
  }
  /* checking which  smart work order items existed in workOrder but now are not present in the form. They have to unassigned*/
  const formAssignedItemsIds = formValues?.assignedItems?.filter(a => !!a.smartLineItemId)?.map(s => s.smartLineItemId)
  const unAssignedItems = [
    ...workOrderAssignedItems?.filter(
      items => !!items.smartLineItemId && !formAssignedItemsIds?.includes(items.smartLineItemId),
    ),
  ]
  return unAssignedItems
}

export const useRemainingLineItems = (swoProjectId?: string | number | null) => {
  const client = useClient(swoPrefix)

  const { data: remainingItems, ...rest } = useQuery<any>(
    ['remainingItems', swoProjectId],
    async () => {
      const response = await client(
        `line-items?isAssigned.equals=false&projectId.equals=${swoProjectId}&size=5000&sort=sortOrder,asc&page=0`,
        {},
      )

      return response?.data
    },
    {
      enabled: !!swoProjectId,
    },
  )

  return {
    remainingItems,
    ...rest,
  }
}

export const useFetchProjectId = (projectId?: string | number | null, uploadedWO?: boolean) => {
  const client = useClient(swoPrefix)
  const [refetchInterval, setRefetchInterval] = useState(15000)

  const { data: swoProject, ...rest } = useQuery<any>(
    ['fetchProjectId', projectId, uploadedWO],
    async () => {
      let response: any

      if (!uploadedWO) {
        response = await client(`projects/projectId/` + projectId + '?createProject=true', {})
      } else {
        response = await client(`projects/projectId/` + projectId, {})
      }

      if (!response?.data || (response?.data && response?.data?.status === 'COMPLETED')) {
        setRefetchInterval(0)
      }
      return response?.data
    },
    {
      enabled: !!projectId,
      refetchInterval: refetchInterval,
    },
  )

  return {
    swoProject,
    ...rest,
  }
}

type AssignArgumentType = {
  swoProjectId: string | number | null | undefined
  showToast?: boolean
  refetchLineItems?: boolean
}

export const useAssignLineItems = (props: AssignArgumentType) => {
  const { swoProjectId, showToast, refetchLineItems } = props
  const client = useClient(swoPrefix)
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    (lineItems: any) => {
      return client(`line-items/list?projectId.equals=${swoProjectId}`, {
        data: lineItems,
        method: 'PUT',
      })
    },
    {
      onSuccess(res: any) {
        if (refetchLineItems) {
          queryClient.invalidateQueries(['remainingItems', swoProjectId])
        }
        if (showToast) {
          toast({
            title: 'Line Items Assignment',
            description: 'Line Items updated successfully.',
            status: 'success',
            isClosable: true,
            position: 'top-left',
          })
        }
      },
      onError(error: any) {
        toast({
          title: 'Assigned Line Items',
          description: (error.title as string) ?? 'Unable to update line items.',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

type CreateArgumentType = {
  swoProject: SWOProject | null | undefined
  showToast?: boolean
  refetchLineItems?: boolean
}

export const useCreateLineItem = (props: CreateArgumentType) => {
  const { swoProject, showToast, refetchLineItems } = props
  const client = useClient(swoPrefix)
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    (lineItems: any) => {
      return client(`line-items/${swoProject?.textractJobId}`, {
        data: lineItems,
        method: 'POST',
      })
    },
    {
      onSuccess(res: any) {
        if (refetchLineItems) {
          queryClient.invalidateQueries(['remainingItems', swoProject?.id])
        }
        if (showToast) {
          toast({
            title: 'Line Items Assignment',
            description: 'Line Items updated successfully.',
            status: 'success',
            isClosable: true,
            position: 'top-left',
          })
        }
      },
      onError(error: any) {
        toast({
          title: 'Assigned Line Items',
          description: (error.title as string) ?? 'Unable to update line items.',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useDeleteLineItems = swoProjectId => {
  const client = useClient(swoPrefix)
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    (payload: { itemIds: string }) => {
      return client('line-items?ids=' + payload.itemIds, {
        method: 'DELETE',
      })
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(['remainingItems', swoProjectId])
        toast({
          title: 'Assigned Items',
          description: 'Item Deleted Successfully',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
      },
      onError(error: any) {
        toast({
          title: 'Assigned Items',
          description: 'Unable to delete Line Items',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useDeleteLineIds = () => {
  const client = useClient()
  const toast = useToast()

  return useMutation(
    (payload: { deletedIds: string }) => {
      return client('work-orders/line-items?ids=' + payload.deletedIds, {
        method: 'DELETE',
      })
    },
    {
      onSuccess() {},
      onError(error: any) {
        toast({
          title: 'Work Order',
          description: 'Unable to delete Line Items',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useAllowLineItemsAssignment = ({ workOrder }) => {
  // commenting this out but this condition will be used in upcoming stories.
  const cancelledStatus = [STATUS.Cancelled].includes(workOrder?.statusLabel?.toLocaleLowerCase() as STATUS)
  const isAssignmentAllowed = !(false || cancelledStatus)
  return { isAssignmentAllowed }
}

export const calculateVendorAmount = (amount, percentage) => {
  amount = Number(amount)
  percentage = Number(!percentage || percentage === '' ? 0 : percentage)
  return round(amount - amount * (percentage / 100), 2)
}

export const calculateProfit = (clientAmount, vendorAmount) => {
  if (clientAmount === 0) return 0
  return round(((clientAmount - vendorAmount) / clientAmount) * 100, 2)
}
/* map to remaining when user unassigns using Unassign Line Item action */

export const mapToRemainingItems = item => {
  return {
    ...item,
    unitPrice: item?.price,
    totalPrice: Number(item?.price) * Number(item?.quantity),
  }
}

/* map to assigned items when user assigns using save on remaining items modal */

export const mapToLineItems = (item, watchPercentage?) => {
  const amount = item.unitPrice * item.quantity
  const percentage = watchPercentage
  return {
    ...item,
    isVerified: false,
    isCompleted: false,
    price: !item.unitPrice || item.unitPrice === '' ? '0' : item.unitPrice,
    document: null,
    profit: percentage,
    clientAmount: amount,
    vendorAmount: calculateVendorAmount(amount, percentage),
  }
}

/* mapping when line item in unassigned and saved. Any changes made to line item will also be saved in swo */
export const mapToUnAssignItem = item => {
  return {
    ...item,
    unitPrice: item?.price,
    id: item.smartLineItemId,
    isAssigned: false,
    totalPrice: Number(item?.price) * Number(item?.quantity),
  }
}

export const PriceInput = props => {
  return <Input {...props} variant="outline" size="sm" />
}

type EditableCellType = {
  valueFormatter?: any
  index: number
  fieldName: string
  formControl: any
  inputType?: string
  fieldArray: string
  updatedItems?: number[]
  setUpdatedItems?: (items) => void
  onChange?: (e, index) => void
  selectedCell: selectedCell | null | undefined
  setSelectedCell: (e) => void
  allowEdit?: boolean
  maxLength?: number
  isRequired?: boolean
}

export const EditableField = (props: EditableCellType) => {
  const {
    index,
    fieldName,
    formControl,
    inputType,
    valueFormatter,
    fieldArray,
    updatedItems,
    setUpdatedItems,
    onChange,
    selectedCell,
    setSelectedCell,
    allowEdit,
    maxLength,
    isRequired,
  } = props
  const {
    getValues,
    setValue,
    control,
    formState: { errors },
  } = formControl
  const values = getValues()
  const remainingItemsWatch = useWatch({ name: fieldArray, control })

  return (
    <>
      {values?.[fieldArray]?.length > 0 && (
        <>
          {selectedCell?.id !== index + '-' + fieldName ? (
            <Box
              pl="3px"
              minH={'20px'}
              minW={'100px'}
              cursor={allowEdit ? 'pointer' : 'default'}
              overflow="hidden"
              textOverflow={'ellipsis'}
              whiteSpace="nowrap"
              data-testid={`cell-` + index + '-' + fieldName}
              onClick={() => {
                if (allowEdit) {
                  setSelectedCell({ id: index + '-' + fieldName, value: remainingItemsWatch[index]?.[fieldName] })
                }
              }}
            >
              {valueFormatter && isValidAndNonEmpty(remainingItemsWatch[index]?.[fieldName])
                ? valueFormatter(remainingItemsWatch[index]?.[fieldName])
                : isValidAndNonEmpty(remainingItemsWatch[index]?.[fieldName])
                ? remainingItemsWatch[index]?.[fieldName]
                : '- - -'}
            </Box>
          ) : (
            <FormControl {...(isRequired ? { isInvalid: !!errors.assignedItems?.[index]?.[fieldName] } : {})}>
              <Controller
                control={control}
                name={`${fieldArray}.${index}.${fieldName}`}
                rules={{ required: isRequired }}
                render={({ field, fieldState }) => (
                  <Input
                    maxLength={maxLength}
                    minW={'80px'}
                    data-testid={`editableField-` + index + '-' + fieldName}
                    key={[fieldName] + '.' + [index]}
                    size="sm"
                    type={inputType}
                    value={field.value}
                    onChange={e => {
                      field.onChange(e.target.value)
                      if (setUpdatedItems && updatedItems && !updatedItems?.includes(values?.[fieldArray][index]?.id)) {
                        setUpdatedItems([...updatedItems, values?.[fieldArray][index]?.id])
                      }
                      onChange?.(e, index)
                    }}
                    onBlur={e => {
                      setSelectedCell(null)
                      if (e.target.value === '') {
                        setValue(`${fieldArray}.${index}.${fieldName}`, selectedCell?.value)
                        onChange?.({ target: { value: selectedCell?.value } }, index)
                      }
                    }}
                  ></Input>
                )}
              ></Controller>
            </FormControl>
          )}
        </>
      )}
    </>
  )
}

type InputFieldType = {
  index: number
  fieldName: string
  formControl: UseFormReturn<any>
  fieldArray: string
  inputType?: string
  onChange?: (e, index) => void
  rules?: any
  maxLength?: number
}
export const InputField = (props: InputFieldType) => {
  const {
    index,
    fieldName,
    formControl,
    fieldArray,
    onChange: handleChange,
    inputType = 'text',
    rules,
    maxLength,
  } = props
  const {
    formState: { errors },
    control,
  } = formControl
  return (
    <>
      <FormControl isInvalid={errors?.[fieldArray] && !!errors?.[fieldArray][index]?.[fieldName]?.message}>
        <Controller
          control={control}
          name={`${fieldArray}.${index}.${fieldName}`}
          rules={rules}
          render={({ field }) => (
            <Input
              maxLength={maxLength}
              key={[fieldName] + '.' + [index]}
              data-testid={`input-` + index + '-' + fieldName}
              size="sm"
              type={inputType}
              value={field.value}
              onChange={e => {
                field.onChange(e.target.value)
                handleChange?.(e, index)
              }}
            ></Input>
          )}
        ></Controller>
        {errors?.remainingItems && (
          <FormErrorMessage>{errors?.[fieldArray][index]?.[fieldName]?.message}</FormErrorMessage>
        )}
      </FormControl>
    </>
  )
}

export const SelectCheckBox = ({ selectedItems, setSelectedItems, row, index }) => {
  return (
    <Checkbox
      data-testid={`check-` + index}
      isChecked={selectedItems?.map(s => s.id)?.includes(row?.id)}
      onChange={e => {
        if (e.currentTarget?.checked) {
          if (!selectedItems?.map(s => s.id).includes(row?.id)) {
            setSelectedItems([...selectedItems, row])
          }
        } else {
          setSelectedItems([...selectedItems.filter(s => s.id !== row?.id)])
        }
      }}
    />
  )
}

export const UploadImage: React.FC<{ label; onClear; onChange; value; testId }> = ({
  label,
  onChange,
  onClear,
  value,
  testId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const onFileChange = event => {
    const file = event.currentTarget.files?.[0]
    onChange?.(file)
    event.target.value = null
  }

  const onFileClear = event => {
    event.stopPropagation()
    onClear?.()
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none', color: 'red' }}
        accept="image/*"
        onChange={onFileChange}
      />
      {!value ? (
        <Button
          ml={1}
          minW={'auto'}
          size="sm"
          data-testid={testId}
          onClick={() => inputRef?.current?.click()}
          colorScheme="darkPrimary"
          variant="outline"
          leftIcon={<BiUpload color="brand.300" />}
          display="flex"
        >
          {t(`${WORK_ORDER}.${label}`)}
        </Button>
      ) : (
        <Box color="brand.300" border="1px solid #345EA6" borderRadius="4px" fontSize="14px">
          <HStack spacing="5px" h="31px" padding="10px" align="center">
            <Text as="span" maxW="70px" isTruncated title="something">
              {value}
            </Text>
            <MdOutlineCancel color="brand.300" cursor="pointer" onClick={onFileClear} />
          </HStack>
        </Box>
      )}
    </Box>
  )
}

export const createInvoicePdf = async ({
  doc,
  property,
  workOrder,
  projectData,
  assignedItems,
  hidePrice,
  onSave,
  locations,
}) => {
  let uploadedImages = [] as any[]
  assignedItems?.forEach(item => {
    const itemImages = item?.document?.filter(doc => !!doc.s3Url).map(i => i.s3Url)
    uploadedImages = [...uploadedImages, ...itemImages]
  })

  const workOrderInfo = [
    { label: 'Start Date:', value: workOrder?.workOrderStartDate ?? '' },
    {
      label: 'Time:',
      value:
        TicketTimeSlots?.find(slots => slots.value?.toString() === workOrder?.workOrderStartTime?.toString())?.label ??
        '',
    },
    { label: 'Priority:', value: projectData?.taskPriority ?? '' },
  ]

  const basicFont = undefined
  const summaryFont = 'Times-Roman'
  const heading = 'Work Order # ' + workOrder?.id
  const startx = 15
  doc.setFontSize(16)
  doc.setFont(basicFont, 'bold')
  doc.text(heading, startx, 20)

  const images = await addImages(['wo-logo-tree.png', ...uploadedImages])
  doc.addImage(images[0], 'png', 160, 5, 35, 35)
  doc.setFontSize(11)
  doc.setFont(summaryFont, 'bold')
  doc.text('Property Address:', startx, 55)
  doc.setFont(summaryFont, 'normal')

  const streetAddress = property?.streetAddress || ''
  const maxLineLength = 30
  let lines = [] as any
  let currentPosition = 0

  while (currentPosition < streetAddress.length) {
    const remainingText = streetAddress.substring(currentPosition)
    const maxLength = Math.min(remainingText.length, maxLineLength)

    // Find the last space character within the maxLength
    let lastSpaceIndex = remainingText.lastIndexOf(' ', maxLength)

    // If no space found, split at maxLength
    if (lastSpaceIndex === -1) {
      lastSpaceIndex = maxLength
    }

    lines.push(remainingText.substring(0, lastSpaceIndex).trim())
    currentPosition += lastSpaceIndex + 1
  }
  lines = lines.slice(0, 4)
  lines.forEach((line, index) => {
    doc.text(line.trim(), startx, 60 + 5 * index)
  })

  //doc.text(property?.streetAddress ? truncateWithEllipsis(property?.streetAddress, 24) : '', startx, 60)
  const lastLineY = 65 + 5 * (lines.length - 1)
  doc.text(property?.city + ' ' + property?.state + ' , ' + property?.zipCode, startx, lastLineY)

  const primaryResident = property?.residents?.find(r => r.primaryResident)
  doc.setFont(summaryFont, 'bold')
  const centerTextX = 75
  doc.text('Resident:', centerTextX, 55)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.firstName + ' ' + primaryResident?.lastName ?? '', centerTextX + 18, 55)
  doc.setFont(summaryFont, 'bold')
  doc.text('Phone:', centerTextX, 60)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.phoneNumber ?? '', centerTextX + 18, 60)
  doc.setFont(summaryFont, 'bold')
  doc.text('Email:', centerTextX, 65)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.emailAddress ?? '', centerTextX + 18, 65)

  const x = 155
  let y = 50

  workOrderInfo.forEach(inv => {
    doc.setFont(summaryFont, 'bold')
    doc.text(inv.label, x + 5, y + 5)
    doc.setFont(summaryFont, 'normal')
    doc.text(
      inv.label === 'Start Date:' || inv.label === 'Expected Completion:' ? dateFormat(inv.value) || '' : inv.value,
      x + 25,
      y + 5,
    )
    y = y + 5
  })

  doc.setFont(summaryFont, 'bold')
  doc.text('Work Type:', startx, y + 25)
  doc.setFont(summaryFont, 'normal')
  doc.text(workOrder?.skillName ?? '', startx + 30, y + 25)
  doc.setFont(summaryFont, 'bold')
  doc.text('Sub Contractor:', startx, y + 30)
  doc.setFont(summaryFont, 'normal')
  doc.text(workOrder.companyName ?? '', startx + 30, y + 30)
  doc.setFont(summaryFont, 'bold')
  doc.text('Total:', x + 5, y + 30)
  doc.setFont(summaryFont, 'normal')
  doc.text(currencyFormatter(workOrder?.finalInvoiceAmount ?? 0), x + 25, y + 30)

  doc.setFont(summaryFont, 'bold')
  autoTable(doc, {
    startY: y + 45,
    headStyles: { fillColor: '#FFFFFF', textColor: '#000000', lineColor: '#000000', lineWidth: 0.1 },
    theme: 'grid',
    bodyStyles: { lineColor: '#000000', minCellHeight: 15 },
    body: [
      ...assignedItems.map(ai => {
        return {
          location: locations?.find(l => l.value?.toString() === ai.location?.toString())?.label,
          description: ai.description,
        }
      }),
    ],
    columns: [
      { header: 'Location', dataKey: 'location' },
      { header: 'Description', dataKey: 'description' },
    ],
  })
  const tableEndsY = (doc as any).lastAutoTable.finalY
  doc.setFontSize(10)
  doc.setFont(basicFont, 'normal')
  let imgX = 15
  let imgY = tableEndsY + 20
  // start new page if there is less space after table ends.
  if (doc.internal.pageSize.getHeight() - imgY < 40) {
    doc.addPage()
    imgY = 5
  }
  images?.splice(0, 1)
  images?.forEach((image, index) => {
    doc.addImage(image, 'png', imgX, imgY, 35, 35)
    if (images?.length - 1 === index) {
      return
    }
    if ((index + 1) % 4 === 0) {
      imgX = 15
      imgY = imgY + 50
      // start new page if there is less space after images.
      if (doc.internal.pageSize.getHeight() - imgY < 40) {
        doc.addPage()
        imgY = 5
      }
    } else {
      imgX = imgX + 50
    }
  })
  doc.save(`${workOrder?.id}_${workOrder?.companyName}_${workOrder?.propertyAddress}.pdf`)
  const pdfUri = doc.output('datauristring')
  onSave([
    {
      documentType: 1010,
      workOrderId: workOrder.id,
      fileObject: pdfUri.split(',')[1],
      fileObjectContentType: 'application/pdf',
      fileType: `${workOrder?.id}_${workOrder?.companyName}_${workOrder?.propertyAddress}.pdf`,
      projectId: projectData?.id,
    },
  ])
}

// !workOrder is a check for new work order modal.
// In case of edit, workorder will be a non-nullable object.
// In case of new work order, it will be null

export const useColumnsShowDecision = ({ workOrder }) => {
  const { isVendor } = useUserRolesSelector()
  const showEditablePrices = !isVendor && !workOrder // Price is editable for non-vendor on new work order modal
  const showReadOnlyPrices = !isVendor && workOrder //price is readonly for vendor and will only show if showPricing is true. Currrently price is also readonly for non-vendor in edit work modal.
  const showVendorPrice = isVendor && workOrder.showPricing
  const showVerification = !isVendor && workOrder
  return {
    showSelect: !isVendor && !workOrder,
    showEditablePrices,
    showReadOnlyPrices,
    showStatus: !!workOrder,
    showImages: !!workOrder,
    showVerification,
    showVendorPrice,
  }
}

export const useActionsShowDecision = ({ workOrder }) => {
  const { isVendor } = useUserRolesSelector()

  return {
    showPriceCheckBox: false,
    notifyVendorCheckBox: !isVendor,
    showMarkAllIsVerified: !isVendor && workOrder,
    showMarkAllIsCompleted: !!workOrder,
    showVerification: !!workOrder,
  }
}

const requiredStyle = {
  color: 'red.500',
  fontWeight: 800,
  fontSize: '18px',
}

export const useFieldEnableDecision = ({ workOrder, lineItems }) => {
  const { isVendor } = useUserRolesSelector()
  const formattedStatus = workOrder?.statusLabel?.toLocaleLowerCase()
  const statusEnabled = isVendor ? [STATUS.New, STATUS.PastDue, STATUS.Approved].includes(formattedStatus as STATUS) : [STATUS.New, STATUS.PastDue, STATUS.Approved, STATUS.Completed, STATUS.Closed].includes(formattedStatus as STATUS)  ;
  const verificationEnabled =
    [STATUS.Completed, STATUS.PastDue, STATUS.New, STATUS.Approved, STATUS.Closed].includes(formattedStatus as STATUS) &&
    lineItems?.some(l => l.isCompleted)

  return {
    statusEnabled: statusEnabled,
    verificationEnabled: verificationEnabled,
  }
}

const setColumnsByConditions = (columns, workOrder, isVendor) => {
  if (workOrder) {
    if (isVendor) {
      if (workOrder.showPricing) {
        columns = columns.filter(c => !['price', 'profit', 'clientAmount', 'isVerified'].includes(c.accessorKey))
      } else {
        columns = columns.filter(
          c => !['price', 'profit', 'clientAmount', 'vendorAmount', 'isVerified'].includes(c.accessorKey),
        )
      }
    }
  } else {
    columns = columns.filter(c => !['isCompleted', 'isVerified', 'images'].includes(c.accessorKey))
  }
  return columns
}

export const useGetLineItemsColumn = ({
  formControl,
  allowEdit,
  assignedItemsArray,
  workOrder,
  onDocumentModalOpen,
  itemsListWithImages,
}) => {
  const [selectedCell, setSelectedCell] = useState<selectedCell | null>(null)
  const { t } = useTranslation()
  const { remove: removeAssigned } = assignedItemsArray
  const { setValue, getValues, watch, control } = formControl
  const values = getValues()
  const watchFieldArray = watch('assignedItems')
  const { isVendor } = useUserRolesSelector()
  const { statusEnabled, verificationEnabled } = useFieldEnableDecision({ workOrder, lineItems: watchFieldArray })
  const controlledAssignedItems = watchFieldArray?.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    }
  })
  const isCheckedArray = Array.isArray(controlledAssignedItems)
    ? controlledAssignedItems?.filter(element => element?.isCompleted)
    : []

  const { projectTypeSelectOptions: locations } = useProjectTypeSelectOptions()

  let columns = useMemo(() => {
    return [
      {
        header: () => <span style={{ marginLeft: '30px' }}> {t(`${WORK_ORDER}.location`)}</span>,
        accessorKey: 'location',
        cell: ({ row }) => {
          const index = row?.index
          const {
            formState: { errors },
            control,
          } = formControl
          const fontSizes = {
            sm: '10px',
            md: '12px',
            lg: '14px',
          }

          const getFontSize = (state: any) => {
            const size = state?.selectProps?.size

            return fontSizes[size] || size
          }

          return (
            <Box pos="relative">
              {index !== 0 && (
                <Box
                  w="50px"
                  pos="absolute"
                  left="-10px"
                  top="10px"
                  _hover={{
                    '.delete-row-icon': { visibility: 'visible' },
                  }}
                >
                  <Icon
                    as={BiXCircle}
                    boxSize={5}
                    data-testid={'unassign-' + index}
                    color="brand.300"
                    visibility="hidden"
                    className="delete-row-icon"
                    onClick={() => {
                      removeAssigned(index)
                    }}
                    cursor="pointer"
                  ></Icon>
                </Box>
              )}
              <FormControl
                ml="27px"
                isInvalid={!!errors.assignedItems?.[index]?.location}
                zIndex={9999 + 1}
                width="130px"
              >
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name={`assignedItems.${index}.location`}
                  render={({ field, fieldState }) => {
                    return (
                      <>
                        <ReactSelect
                          {...field}
                          options={locations}
                          size="md"
                          value={field.value}
                          isDisabled={isVendor}
                          onChange={option => {
                            field.onChange(option)
                          }}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 99999, position: 'fixed' }),
                          }}
                          chakraStyles={{
                            container: (provided: any) => {
                              return {
                                ...provided,
                                pointerEvents: 'auto',
                                // background: '#F7FAFC',
                              }
                            },
                            placeholder: provider => ({
                              ...provider,
                              fontSize: '12px',
                              color: 'gray.600',
                              fontWeight: 400,
                            }),
                            menuList: (provided: any, state: any) => {
                              return { ...provided }
                            },
                            menu: (provided: any, state: any) => {
                              return {
                                ...provided,
                                boxShadow: 'lg',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderColor: 'gray.200',
                                borderRadius: 'md',
                                bg: 'white',
                                margin: '2px',
                                maxHeight: '115px !important',
                                overflowY: 'scroll',                                                            
                              }
                            },

                            singleValue: (provider: any) => ({
                              ...provider,
                              color: '#2D3748',
                              fontWeight: '400',
                            }),
                            option: (provider: any, state: any) => {
                              return {
                                ...provider,
                                fontSize: getFontSize(state),
                                bg: state.isSelected ? 'gray.50' : 'white',
                                _hover: {
                                  bg: state.isSelected ? 'gray.50' : 'blue.50',
                                },
                                color: state.isSelected ? 'gray.800' : '',
                                display: state.data?.isHidden ? 'none' : 'block',
                              }
                            },
                            valueContainer(provided: any) {
                              const px = {
                                sm: '12px',
                                md: '16px',
                                lg: '16px',
                              }

                              return {
                                ...provided,
                                padding: `0.125rem ${px['sm']}`,
                                color: 'gray.500',
                              }
                            },

                            dropdownIndicator: provided => ({
                              ...provided,
                              backgroundColor: 'transparent',
                              '&>svg': {
                                color: 'gray.600',
                              },
                            }),
                            control: (provider: any, state) => {
                              const isBorderLeft = true

                              const borderLeftStyle = isBorderLeft ? inputBorderLeftStyle : {}

                              return {
                                ...provider,
                                ...borderLeftStyle,
                                borderRadius: '6px',
                                fontSize: getFontSize('sm'),
                                _focus: inputFocusStateStyle,
                                _disabled: {
                                  opacity: 0.7,
                                  cursor: 'not-allowed',
                                  bg: 'gray.100',
                                },
                              }
                            },
                          }}
                          key={'assignedItems.' + [index]}
                          menuPosition="fixed"
                          menuPortalTarget={document.body}
                          menuShouldScrollIntoView={false}
                          isSearchable={true}
                          placeholder={'Select'}
                          components={{
                            IndicatorSeparator: null,
                            SingleValue: option => {
                              return (
                                <Flex title={option.children as string} position="absolute" cursor="default !important">
                                  <Text isTruncated whiteSpace="nowrap" maxW="148px" fontSize="12px">
                                    {option.children}
                                  </Text>
                                </Flex>
                              )
                            },
                          }}
                        />
                      </>
                    )
                  }}
                />
              </FormControl>
            </Box>
          )
        },
      },

      {
        header: () => {
          return (
            <>
              {!isVendor && !workOrder && (
                <Box as="span" sx={requiredStyle}>
                  *
                </Box>
              )}
              <Box ml="40px">{t(`${WORK_ORDER}.details`)}</Box>
            </>
          )
        },
        accessorKey: 'description',
        cell: cellInfo => {
          const index = cellInfo?.row?.index
          return (
            <Box>
              <EditableField
                index={index}
                maxLength={2000}
                selectedCell={selectedCell}
                setSelectedCell={setSelectedCell}
                fieldName="description"
                fieldArray="assignedItems"
                formControl={formControl}
                inputType="text"
                allowEdit={allowEdit}
                isRequired={true}
              />
            </Box>
          )
        },
        size: 250,
      },
      {
        header: `${WORK_ORDER}.complete`,
        accessorKey: 'isCompleted',
        enableSorting: false,
        cell: cellInfo => {
          const index = cellInfo?.row?.index
          return (
            <HStack justifyContent={'center'} h="28px">
              <Controller
                control={control}
                name={`assignedItems.${index}.isCompleted`}
                render={({ field, fieldState }) => (
                  <CustomCheckBox
                    testid={`isCompleted-` + index}
                    // text="Completed"
                    isChecked={field.value}
                    disabled={!statusEnabled}
                    onChange={e => {
                      if (!e.target.checked) {
                        setValue(`assignedItems.${index}.isVerified`, false)
                      }
                      field.onChange(e.currentTarget.checked)
                    }}
                  ></CustomCheckBox>
                )}
              ></Controller>
            </HStack>
          )
        },
      },
      {
        header: `${WORK_ORDER}.images`,
        accessorKey: 'images',
        enableSorting: false,
        cell: cellInfo => {
          const index = cellInfo?.row?.index
          let result = ''
          if (itemsListWithImages && Symbol.iterator in Object(itemsListWithImages)) {
            const hasIndex = [...itemsListWithImages].includes(index)
            result = hasIndex ? t('viewFiles') : t('chooseFile')
          } else {
            result = t('chooseFile')
          }
          const showError =
            controlledAssignedItems?.[index]?.document?.length === 0 &&
            controlledAssignedItems?.[index]?.isCompleted &&
            isVendor
          return (
            <Box p={1}>
              <Button
                variant="outline"
                colorScheme="brand"
                size="sm"
                onClick={e => {
                  onDocumentModalOpen(e, index)
                }}
              >
                {result}
              </Button>
              {showError && (
                <Text fontSize="xs" color={'red'}>
                  It is required.
                </Text>
              )}
            </Box>
          )
        },
      },
      {
        header: `${WORK_ORDER}.verification`,
        accessorKey: 'isVerified',
        enableSorting: false,
        cell: cellInfo => {
          const index = cellInfo?.row?.index
          return (
            <HStack justifyContent={'center'} h="50px" mr="23px">
              <Controller
                control={control}
                name={`assignedItems.${index}.isVerified`}
                render={({ field, fieldState }) => (
                  <CustomCheckBox
                    text="Verified"
                    disabled={!(values.assignedItems?.[index]?.isCompleted && verificationEnabled)}
                    isChecked={field.value}
                    testid={`isVerified-` + index}
                    onChange={e => {
                      field.onChange(e.currentTarget.checked)
                    }}
                  ></CustomCheckBox>
                )}
              ></Controller>
            </HStack>
          )
        },
      },
    ]
  }, [
    selectedCell,
    setSelectedCell,
    verificationEnabled,
    statusEnabled,
    controlledAssignedItems?.length,
    locations?.length,
    itemsListWithImages?.length,
    isCheckedArray.length,
  ])
  columns = setColumnsByConditions(columns, workOrder, isVendor)
  return columns
}
